import { IconCalendarMark, IconCameraVideo, IconContact, IconRabbit,
  IconScrewdriverWrench, IconSparkle, IconStrategyTasks, IconTransferTruckTime,
  IconWarehouse1,
} from '@taki/ui';
import type { ShellAppRegistry } from '@taki/shell';
import { ContactsScreen } from './apps/contact/ContactsScreen.js';
import { CatManagerSixScreen } from './apps/cat-manager-six/CatManagerSixScreen.js';
import { CatLoggerOutsideScreen } from './apps/cat-logger-outside/CatLoggerOutsideScreen.js';
import { CatHousesScreen } from './apps/cat-houses/CatHousesScreen.js';
import { UiLabScreen } from './apps/ui-lab/UiLabScreen.js';
import { InventaireScreen } from './apps/inventaire/InventaireScreen.js';
import { ReservationsScreen } from './apps/reservations/ReservationsScreen.js';
import { MaintenanceScreen } from './apps/maintenance/MaintenanceScreen.js';

/**
 * Registre des apps montees par le shell.
 *
 * LE MODELE A COPIER EST `inventaire` : c'est l'ecran de reference d'une
 * COLLECTION (useScreen + ViewHost + `views.ts` partage avec `settings.tsx`).
 * `contact` est l'exemple MINIMAL, sans sections, mais sur des donnees reelles.
 * Les trois apps `cat-*` sont DEPRECATED (champs `deprecated` et
 * `deprecatedSince` de leur fiche `taki.app.json`) : elles dessinent encore
 * leurs listes a la main, ce que la doctrine interdit depuis les vues du socle.
 * Elles restent montees pour la demo, elles ne se copient JAMAIS.
 * Chaque app se distingue par son ICONE, jamais par une couleur de fond.
 *
 * Le `label` est un texte d'AFFICHAGE : il ne porte aucun etat de l'app. Une app
 * deprecated se declare dans sa fiche `taki.app.json` (`deprecated` +
 * `deprecatedSince`), jamais par un prefixe ecrit a la main dans ce libelle :
 * `pnpm apps:check` echoue si le marqueur reapparait quelque part.
 *
 * Le numero de version n'a rien a faire ici non plus : la fiche est la seule
 * source de verite, et c'est l'outil de release qui l'ecrit (docs/PLAYBOOK-APP.md section 4).
 *
 * Les ids correspondent aux entrees de `installedApps` dans `taki.config.json`.
 * Le shell monte chaque app paresseusement via `load()`, ses reglages via
 * `loadSettings()` (hub Parametres, /settings) ; `actions` declare les
 * creations proposees comme shortcuts du dock (iconName = nom d'une icone
 * de la map curatee DOCK_ICONS du shell).
 */
export const demoRegistry: ShellAppRegistry = {
  'cat-houses': {
    id: 'cat-houses',
    label: 'Cat Houses',
    icon: <IconWarehouse1 size={22} />,
    load: async () => ({ default: CatHousesScreen }),
    loadSettings: () => import('./apps/cat-houses/settings.js'),
    actions: [
      { id: 'new-house', label: 'Nouvelle maison', iconName: 'IconHome' },
      { id: 'new-task', label: 'Nouveau ticket', iconName: 'IconPlus' },
    ],
  },
  contact: {
    id: 'contact',
    label: 'Contact',
    icon: <IconContact size={22} />,
    load: async () => ({ default: ContactsScreen }),
    loadSettings: () => import('./apps/contact/settings.js'),
    actions: [{ id: 'new', label: 'Nouveau contact', iconName: 'IconPlus' }],
  },
  'cat-manager-six': {
    id: 'cat-manager-six',
    label: 'Cat Manager Six',
    icon: <IconRabbit size={22} />,
    load: async () => ({ default: CatManagerSixScreen }),
    loadSettings: () => import('./apps/cat-manager-six/settings.js'),
    actions: [
      { id: 'add-cat', label: 'Ajouter un chat', iconName: 'IconPlus' },
      { id: 'add-visit', label: 'Nouvelle reservation', iconName: 'IconCalendarAdd' },
    ],
  },
  'cat-logger-outside': {
    id: 'cat-logger-outside',
    label: 'Cat Logger Outside',
    icon: <IconTransferTruckTime size={22} />,
    load: async () => ({ default: CatLoggerOutsideScreen }),
    loadSettings: () => import('./apps/cat-logger-outside/settings.js'),
    actions: [
      { id: 'new-outing', label: 'Nouvelle sortie', iconName: 'IconCalendarAdd' },
      { id: 'new-cat', label: 'Ajouter un chat', iconName: 'IconPlus' },
    ],
  },
  /*
   * PACK DE DEMONSTRATION "location de materiel cinema" : trois apps qui
   * partagent le meme vocabulaire de donnees (assets `materiel`, bookings
   * `location`, tickets `panne`, workOrders `reparation`) pour simuler le
   * systeme d'information complet d'un loueur. Chacune porte son jeu de
   * demonstration importable depuis ses reglages ; l'ordre d'import est
   * Inventaire, puis Reservations, puis Maintenance (les deux dernieres
   * s'accrochent au materiel de la premiere par sa cle de demonstration).
   */
  cockpit: {
    id: 'cockpit',
    label: 'Cockpit',
    icon: <IconStrategyTasks size={22} />,
    /*
     * SEULE ENTREE EN IMPORT PARESSEUX du registre, et c'est voulu : le cockpit
     * pese plusieurs milliers de lignes qui ne servent qu'a nous. Un import
     * statique les embarquerait dans le bundle de CHAQUE client, meme la ou
     * l'app n'est pas installee. `import()` les met dans un morceau a part, que
     * le navigateur ne va chercher que si on ouvre l'app.
     *
     * Sa vraie porte de sortie reste `installedApps` du `taki.config.json` :
     * une config client qui ne cite pas `cockpit` ne le monte jamais.
     */
    load: () => import('./apps/cockpit/CockpitScreen.js'),
    loadSettings: () => import('./apps/cockpit/settings.js'),
    actions: [
      { id: 'new-chantier', label: 'Nouveau chantier', iconName: 'IconPlus' },
      { id: 'new-brouillon', label: "Nouveau projet d'app", iconName: 'IconPlus' },
    ],
  },
  inventaire: {
    id: 'inventaire',
    label: 'Inventaire',
    icon: <IconCameraVideo size={22} />,
    load: async () => ({ default: InventaireScreen }),
    loadSettings: () => import('./apps/inventaire/settings.js'),
    actions: [{ id: 'new', label: 'Nouveau materiel', iconName: 'IconPlus' }],
  },
  reservations: {
    id: 'reservations',
    label: 'Reservations',
    icon: <IconCalendarMark size={22} />,
    load: async () => ({ default: ReservationsScreen }),
    loadSettings: () => import('./apps/reservations/settings.js'),
    actions: [
      { id: 'new', label: 'Nouvelle reservation', iconName: 'IconCalendarAdd' },
    ],
  },
  maintenance: {
    id: 'maintenance',
    label: 'Maintenance',
    icon: <IconScrewdriverWrench size={22} />,
    load: async () => ({ default: MaintenanceScreen }),
    loadSettings: () => import('./apps/maintenance/settings.js'),
    actions: [
      { id: 'new-ticket', label: 'Nouveau signalement', iconName: 'IconPlus' },
      { id: 'new-intervention', label: 'Nouvelle intervention', iconName: 'IconPlus' },
    ],
  },
  // App de DEMONSTRATION (vitrine du socle @taki/ui) : aucune donnee reelle,
  // aucune action de dock. Ses reglages montrent la ligne "vue par defaut"
  // telle qu'une vraie app la pose.
  'ui-lab': {
    id: 'ui-lab',
    label: 'UI Lab',
    icon: <IconSparkle size={22} />,
    load: async () => ({ default: UiLabScreen }),
    loadSettings: () => import('./apps/ui-lab/settings.js'),
  },
};

export default demoRegistry;
